import { Link, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { RailSectionTitle } from "../../../components/discovery/DesktopExploreRail";
import { DesktopRoomLine } from "../../../components/room/DesktopRoomLine";
import { Tag } from "../../../components/ui/Tag";
import { createMemoryStore } from "../../../lib/store";
import { useApiQuery } from "../../../services/api";
import { useActiveLang } from "../../../domains/app/hooks/useActiveLang";
import { getItemTranslation } from "../../../domains/app/utils/getItemTranslation";
import { useGetRecommendedPlaylistsQuery } from "../../../domains/playlist/hooks/useGetRecommendedPlaylistsQuery";
import type { RoomResourceListItem } from "../../../domains/room/hooks/useGetRoomResourceItemsQuery";
import { getRoomName } from "../../../domains/room/hooks/useRoomName";
import { getRoomLogoUrl } from "../../../domains/room/utils/getRoomLogoUrl";
import { roomResourceType } from "../../../domains/room/utils/roomResourceItem";
import type { PrivateRoom } from "../../../domains/room/privateRoom";
import { gaEventSetTopicFilter } from "../../../tools/analytics/domains/filter.analytics";
import { roomLineSubtitle } from "../../../domains/room/utils/roomContentCounts";

/** How many entries each rail list shows. */
const RAIL_PREVIEW = 3;
/** How many topics show before "+N". */
const TOPIC_PREVIEW = 6;

// Rooms whose topic list the reader unfolded with "+N", for this session.
const useUnfoldedTopics = createMemoryStore<ReadonlyArray<string>>([]);

/**
 * The room's topics: its curated filter topics, or its tags for a room not
 * migrated to topics yet (the same two sources as the room's filter).
 */
export function roomTopics(
  room: PrivateRoom
): ReadonlyArray<{ key: string; label: string }> {
  return room.filterTopicModels.length > 0
    ? room.filterTopicModels.map((topic) => ({
        key: topic.uid,
        label: getItemTranslation(topic),
      }))
    : room.tagModels.map((tag) => ({
        key: tag.id,
        label: getItemTranslation(tag),
      }));
}

export function resourceTypeLabel(
  item: RoomResourceListItem,
  t: (key: string) => string
): string {
  const type = roomResourceType(item);
  switch (type.kind) {
    case "label":
      return type.label;
    case "article":
      return t("Article");
    case "collection":
      return t("Collection");
    case "podcast":
      return t("Podcast");
    default:
      throw new Error(type satisfies never);
  }
}

/**
 * The room's topics, as chips. A chip filters the room's Discovery list to
 * that topic; the selected one unselects it.
 */
export function RoomTopicsSection({
  room,
  selectedTopic,
}: {
  room: PrivateRoom;
  selectedTopic: string | null;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const [unfolded, setUnfolded] = useUnfoldedTopics();
  const topics = roomTopics(room);
  if (topics.length === 0) return null;
  const isUnfolded = unfolded.includes(room.id);
  const shown = isUnfolded ? topics : topics.slice(0, TOPIC_PREVIEW);
  return (
    <section className="flex flex-col gap-3">
      <RailSectionTitle title={t("Topics")} onSeeAll={null} />
      <div className="flex flex-wrap gap-1.5">
        {shown.map((topic) => (
          <Link
            key={topic.key}
            to={
              topic.key === selectedTopic
                ? `/room/discovery/${room.id}`
                : `/room/discovery/${room.id}?topic=${encodeURIComponent(topic.key)}`
            }
            replace
            aria-current={topic.key === selectedTopic ? "true" : undefined}
            onClick={() => {
              if (topic.key === selectedTopic) return;
              gaEventSetTopicFilter({
                keys: [topic.key],
                matchMode: "any",
                roomName: room.translations.en.name,
              });
            }}
            className="no-underline"
          >
            <Tag
              variant={topic.key === selectedTopic ? "pine" : "neutral"}
              size="md"
              state="idle"
              icon={null}
            >
              {topic.label}
            </Tag>
          </Link>
        ))}
        {shown.length === topics.length ? null : (
          <button
            type="button"
            onClick={() => {
              setUnfolded([...unfolded, room.id]);
            }}
            className="cursor-pointer border-none bg-transparent p-0"
          >
            <Tag variant="neutral" size="md" state="idle" icon={null}>
              {t("+{{count}}", { count: topics.length - shown.length })}
            </Tag>
          </button>
        )}
      </div>
    </section>
  );
}

/** The room's collections: the first three, by the room's recommendation. */
export function RoomCollectionsSection({
  room,
}: {
  room: PrivateRoom;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const history = useHistory();
  const { playlists } = useGetRecommendedPlaylistsQuery(room.id);
  if (playlists.length === 0) return null;
  return (
    <section className="flex flex-col gap-3">
      <RailSectionTitle title={t("Collections")} onSeeAll={null} />
      {playlists.slice(0, RAIL_PREVIEW).map((playlist) => {
        const image = playlist.images.at(0);
        return (
          <button
            key={playlist.id}
            type="button"
            onClick={() => {
              history.push(`/room/playlist/${room.id}/${playlist.id}`);
            }}
            className="flex w-full cursor-pointer items-start gap-3 border-none bg-transparent p-0 text-left"
          >
            {image === undefined ? (
              <span className="h-12 w-18 shrink-0 rounded-lg bg-ds-slate-100" />
            ) : (
              <img
                src={image.sizes === null ? image.url : image.sizes.small.file}
                alt=""
                className="h-12 w-18 shrink-0 rounded-lg object-cover"
              />
            )}
            <span className="flex min-w-0 flex-col gap-0.5">
              <span className="line-clamp-3 text-footnote font-semibold text-ds-slate-950 hover:underline">
                {playlist.title}
              </span>
              <span className="text-caption text-ds-slate-600">
                {t("{{count}} articles", { count: playlist.metrics.articles })}
              </span>
            </span>
          </button>
        );
      })}
    </section>
  );
}

/** The other rooms the reader follows, and the way to the directory. */
export function OtherRoomsSection({
  room,
}: {
  room: PrivateRoom;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const history = useHistory();
  const { activeLang, contentLang } = useActiveLang();
  const joinedRoomsQuery = useApiQuery("/webapp/private/rooms", {});
  if (joinedRoomsQuery.state !== "success") return null;
  const isMember = joinedRoomsQuery.body.rooms.some(
    (joined) => joined.id === room.id
  );
  const others = joinedRoomsQuery.body.rooms
    .filter((joined) => joined.id !== room.id)
    .slice(0, RAIL_PREVIEW);
  if (others.length === 0) return null;
  return (
    <section className="flex flex-col gap-3">
      <RailSectionTitle
        title={isMember ? t("Your other rooms") : t("Your rooms")}
        onSeeAll={null}
      />
      {others.map((other) => (
        <DesktopRoomLine
          nameBadge={null}
          key={other.id}
          name={getRoomName(other, activeLang)}
          logoUrl={getRoomLogoUrl(other)}
          subtitle={roomLineSubtitle(other, contentLang, t)}
          action={null}
          onClick={() => {
            history.push(`/room/discovery/${other.id}`);
          }}
        />
      ))}
      <Link
        to="/rooms"
        className="text-footnote font-semibold text-ds-mint-700 no-underline hover:underline"
      >
        {t("All rooms")}
      </Link>
    </section>
  );
}
